// The access filter predicates, in the two forms the map page needs.
//
// The /map page filters twice over the same parks: once in JavaScript, to
// build the card list and the "Showing X of Y" count, and once as a MapLibre
// filter expression, to decide which circles the canvas draws. Those are two
// different evaluators and they cannot share an implementation, so they share
// this module instead: one set of thresholds, one set of sentinels, and a
// test that runs both over the same fixtures and asserts identical verdicts.
//
// This exists because the two halves have already disagreed once. The access
// filters reached the list and not the canvas, so turning on "no steps"
// shrank the list while the map kept drawing every park.
//
// The invariant both forms encode: a park with no mapped access data is
// EXCLUDED while a filter is active, never counted as passing it. An unmapped
// park has not been shown to have step-free paths; it has been shown to have
// no surveyor. Offering it as a match would make the map a picture of
// OpenStreetMap coverage rather than of parks.

export type AccessFilters = {
  minPavedPct: number;
  noSteps: boolean;
  maxWalkToSeatM: number;
  parkingNearby: boolean;
};

export const EMPTY_ACCESS: AccessFilters = {
  minPavedPct: 0,
  noSteps: false,
  maxWalkToSeatM: 0,
  parkingNearby: false,
};

export function accessFilterCount(f: AccessFilters): number {
  return (
    (f.minPavedPct > 0 ? 1 : 0) +
    (f.noSteps ? 1 : 0) +
    (f.maxWalkToSeatM > 0 ? 1 : 0) +
    (f.parkingNearby ? 1 : 0)
  );
}

// Sentinels for the MapLibre form. MapLibre has no `typeof` guard as
// convenient as the JS one, so each clause coalesces a missing or null value
// to something that cannot satisfy its comparison. build-cache omits the two
// nullable keys entirely and writes null for the two counts, so both shapes
// have to fall through to the sentinel.
const NO_SHARE = -1; // fails every `>= minPavedPct`
const NO_COUNT = -1; // fails `== 0`
const NO_DISTANCE = 1e9; // fails every `<= maxWalkToSeatM`
const NO_LOTS = 0; // fails `> 0`

// The subset of a park summary these filters read. Kept structural rather
// than importing ParkSummary so the parity test can build minimal fixtures.
export type AccessFilterable = {
  accessPavedShare?: number | null;
  accessStepsCount?: number | null;
  accessWalkToSeatP90M?: number | null;
  accessParkingWithin200m?: number | null;
};

/** JS form: does this park survive the active access filters? */
export function parkPassesAccessFilters(p: AccessFilterable, f: AccessFilters): boolean {
  if (f.minPavedPct > 0) {
    if (typeof p.accessPavedShare !== "number") return false;
    if (p.accessPavedShare * 100 < f.minPavedPct) return false;
  }
  if (f.noSteps) {
    if (typeof p.accessStepsCount !== "number") return false;
    if (p.accessStepsCount > 0) return false;
  }
  if (f.maxWalkToSeatM > 0) {
    if (typeof p.accessWalkToSeatP90M !== "number") return false;
    if (p.accessWalkToSeatP90M > f.maxWalkToSeatM) return false;
  }
  if (f.parkingNearby) {
    if (typeof p.accessParkingWithin200m !== "number") return false;
    if (p.accessParkingWithin200m === 0) return false;
  }
  return true;
}

// A MapLibre filter expression fragment. Typed loosely on purpose: this
// module is imported by a plain node test as well as by the map component,
// so it must not pull in maplibre-gl's types at runtime.
export type FilterClause = unknown[];

/** MapLibre form: the clauses for the active access filters, or [] if none. */
export function accessFilterClauses(f: AccessFilters): FilterClause[] {
  const parts: FilterClause[] = [];
  if (f.minPavedPct > 0) {
    parts.push([">=", ["*", ["coalesce", ["get", "accessPavedShare"], NO_SHARE], 100], f.minPavedPct]);
  }
  if (f.noSteps) {
    parts.push(["==", ["coalesce", ["get", "accessStepsCount"], NO_COUNT], 0]);
  }
  if (f.maxWalkToSeatM > 0) {
    parts.push(["<=", ["coalesce", ["get", "accessWalkToSeatP90M"], NO_DISTANCE], f.maxWalkToSeatM]);
  }
  if (f.parkingNearby) {
    parts.push([">", ["coalesce", ["get", "accessParkingWithin200m"], NO_LOTS], 0]);
  }
  return parts;
}
